import { captureException } from '@/utils/sentry';
import { GooglePlaces } from '@domain/addresses/blocs/services/google-places';
import { useCallback, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

const DEBOUNCE_DELAY_MS = 500;

export interface AddressSearchResult {
  description: string;
  key: string;
  label: string;
  placeId: string;
}

export interface UseGoogleApiAddressSearchLoaderOptions {
  /** Rectangle to bias autocomplete predictions toward (e.g. current map viewport). */
  locationBias?: google.maps.LatLngBoundsLiteral;
  /** Invoked when address search fails. */
  onError?: (error: unknown) => void;
  /** Google Places autocomplete prediction types, e.g. ['geocode']. */
  types?: string[];
}

export interface UseGoogleApiAddressSearchResult {
  /** Array of address search results from the autocomplete API. */
  addressResults: AddressSearchResult[];
  /** Clears the search results and resets state. */
  clear: () => void;
  /** True when the most recent search failed. */
  hasError: boolean;
  /** Boolean indicating if a search is in progress. */
  isSearching: boolean;
  /** The last successfully searched input text. */
  lastSearched: string;
  /** Immediately re-runs the last attempted search. */
  retry: () => void;
  /** Initiates a debounced search for the given text. */
  search: (text: string) => void;
}

/**
 * Loader to retrieve addresses from google places autocomplete API.
 *
 * @param options {@link UseGoogleApiAddressSearchLoaderOptions}
 *
 * @returns {@link UseGoogleApiAddressSearchResult}
 */
export const useGoogleApiAddressSearchLoader = ({
  locationBias,
  onError,
  types,
}: UseGoogleApiAddressSearchLoaderOptions = {}): UseGoogleApiAddressSearchResult => {
  const [addressResults, setAddressResults] = useState<AddressSearchResult[]>(
    [],
  );
  const [hasError, setHasError] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [lastSearched, setLastSearched] = useState('');

  // Refs so the debounced search reads the latest viewport/types without
  // resetting the debounce timer each time the map pans.
  const locationBiasRef = useRef(locationBias);
  locationBiasRef.current = locationBias;
  const typesRef = useRef(types);
  typesRef.current = types;

  const lastAttemptedRef = useRef('');

  const debouncedSearch = useDebouncedCallback(async (input: string) => {
    if (!input.trim()) {
      setAddressResults([]);
      setIsSearching(false);
      return;
    }

    try {
      const predictions = await GooglePlaces.getPlaceAutocomplete(
        input,
        typesRef.current,
        locationBiasRef.current,
      );
      const results: AddressSearchResult[] = predictions.map((prediction) => {
        const { description, place_id, structured_formatting } = prediction;
        const { main_text } = structured_formatting || {};
        return {
          description: description,
          key: place_id,
          label: main_text || description,
          placeId: place_id,
        };
      });
      setAddressResults(results);
      setLastSearched(input);
    } catch (error) {
      captureException(error);
      onError?.(error);
      setAddressResults([]);
      setHasError(true);
    } finally {
      setIsSearching(false);
    }
  }, DEBOUNCE_DELAY_MS);

  const search = useCallback(
    (text: string) => {
      lastAttemptedRef.current = text;
      setHasError(false);
      setIsSearching(true);
      debouncedSearch(text);
    },
    [debouncedSearch],
  );

  const retry = useCallback(() => {
    if (!lastAttemptedRef.current.trim()) {
      return;
    }
    setHasError(false);
    setIsSearching(true);
    debouncedSearch(lastAttemptedRef.current);
    debouncedSearch.flush();
  }, [debouncedSearch]);

  const clear = useCallback(() => {
    debouncedSearch.cancel();
    lastAttemptedRef.current = '';
    setAddressResults([]);
    setHasError(false);
    setIsSearching(false);
    setLastSearched('');
  }, [debouncedSearch]);

  return {
    addressResults,
    clear,
    hasError,
    isSearching,
    lastSearched,
    retry,
    search,
  };
};
